import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, type SxProps, type Theme, Typography } from '@mui/material';
import { mergeSx } from 'merge-sx';

import { LocationDTO, OrganizationDTO } from '@/api/generated.ts';
import { borders } from '@/theme/constants.ts';
import typography from '@/theme/typography.ts';

import LocationItem from './LocationItem.tsx';
import LocationPathPopper from './LocationPathPopper.tsx';
import { SimpleItem } from './types.ts';

interface LocationMultiDisplayProps {
  locations?: LocationDTO[];
  organization?: OrganizationDTO;
  sx?: SxProps<Theme>;
  colorized?: boolean;
  useInsideDialog?: boolean;
  placeholder?: string | React.ReactNode;
  rootLinkTo?: string;
  outlined?: boolean;
  showFullTree?: boolean;
  newItem?: SimpleItem;
  maxItemWidth?: number;
}

const LocationMultiDisplay: React.FC<LocationMultiDisplayProps> = ({
  locations,
  organization,
  sx,
  colorized,
  placeholder,
  useInsideDialog
}) => {
  const { t } = useTranslation('processes');

  if (!locations?.length) {
    return (
      placeholder || (
        <Typography
          sx={mergeSx<Theme>(
            {
              ...typography.body2,
              color: 'grey.700'
            },
            sx
          )}
        >
          {t('layouts.location.placeholder', { count: 1 })}
        </Typography>
      )
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        sx={mergeSx<Theme>(
          {
            ...typography.body2,
            '& .LocationItem-draft-outer': {
              borderColor: 'primary.500',
              borderStyle: 'dashed'
            },
            '& .LocationItem-draft .LocationItem-icon': {
              color: 'primary.500'
            },
            '& .LocationItem-draft .LocationItem-label': {
              color: 'primary.500'
            }
          },
          useInsideDialog && {
            '& .LocationItem-label': {
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              maxWidth: 70
            }
          },
          sx
        )}
      >
        <Stack
          key="locations"
          direction="row"
          alignItems="center"
          gap={1}
          sx={{
            whiteSpace: 'nowrap',
            textDecoration: 'none',
            '& .LocationItem-root': {
              height: 24,
              borderRadius: 40,
              border: borders.default,
              borderColor: 'grey.400',
              py: '1px',
              px: 1
            },
            '& .LocationItem-root .LocationItem-label': {
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              maxWidth: 100
            },
            '& .LocationItem-icon': {
              color: colorized ? 'primary.main' : 'grey.800'
            },
            '& .LocationItem-label': {
              color: colorized ? 'primary.main' : 'text.primary'
            }
          }}
        >
          {(locations || []).map(
            location =>
              organization && (
                <LocationPathPopper
                  key={location.slug}
                  locationSlug={location.slug!}
                  organizationSlug={organization.slug!}
                >
                  <LocationItem type={location.type} name={location.name} />
                </LocationPathPopper>
              )
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default LocationMultiDisplay;
